import { useEffect, useMemo, useState } from 'react';

import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

export default function useDashboardPerguntas(
  entidadeId,
  { enabled = true, filtros, formularioId, refreshToken = 0 } = {}
) {
  const [isLoadingDados, setIsLoadingDados] = useState(false);
  const [isLoadingFormulario, setIsLoadingFormulario] = useState(false);
  const [dados, setDados] = useState(null);
  const [metadadosPorPergunta, setMetadadosPorPergunta] = useState({});

  useEffect(() => {
    if (!enabled || !entidadeId) {
      setDados(null);
      return undefined;
    }

    let ativo = true;

    const carregarDados = async () => {
      setIsLoadingDados(true);
      try {
        const response = await http.get(
          '/FormularioGlobalDashboard/ObterEstatisticasPerguntas',
          { params: { entidadeId, ...filtros } }
        );

        if (ativo) {
          setDados(response.data);
        }
      } catch (err) {
        if (ativo) {
          errorHandler(err);
        }
      } finally {
        if (ativo) {
          setIsLoadingDados(false);
        }
      }
    };

    carregarDados();

    return () => {
      ativo = false;
    };
  }, [enabled, entidadeId, filtros, refreshToken]);

  useEffect(() => {
    if (!enabled || !formularioId) {
      setIsLoadingFormulario(false);
      setMetadadosPorPergunta({});
      return undefined;
    }

    let ativo = true;

    const carregarFormulario = async () => {
      setIsLoadingFormulario(true);
      setMetadadosPorPergunta({});
      try {
        const response = await http.get('/FormularioGlobal/Edit', {
          params: { id: formularioId },
        });
        const formulario = response.data?.model || response.data;
        const metadados = {};

        (formulario?.perguntas || []).forEach((pergunta) => {
          metadados[pergunta.id] = {
            ordemFormulario: pergunta.ordem,
            permitirComentario: !!pergunta.permitirComentario,
            secao: null,
          };
        });

        (formulario?.secoes || []).forEach((secao) => {
          (secao.perguntas || []).forEach((pergunta) => {
            metadados[pergunta.id] = {
              ordemFormulario: pergunta.ordem,
              permitirComentario: !!pergunta.permitirComentario,
              secao: {
                id: secao.id,
                titulo: secao.titulo,
                descricao: secao.descricao,
                ordem: secao.ordem,
              },
            };
          });
        });

        if (ativo) {
          setMetadadosPorPergunta(metadados);
        }
      } catch (err) {
        if (ativo) {
          setMetadadosPorPergunta({});
          errorHandler(err);
        }
      } finally {
        if (ativo) {
          setIsLoadingFormulario(false);
        }
      }
    };

    carregarFormulario();

    return () => {
      ativo = false;
    };
  }, [enabled, formularioId]);

  const dadosComMetadados = useMemo(() => {
    if (!dados) return null;

    return {
      ...dados,
      perguntas: (dados.perguntas || []).map((pergunta) => {
        const metadados = metadadosPorPergunta[pergunta.perguntaId];

        return {
          ...pergunta,
          ordemFormulario:
            metadados?.ordemFormulario ??
            pergunta.ordemFormulario ??
            pergunta.ordem,
          permitirComentario:
            metadados?.permitirComentario ?? !!pergunta.permitirComentario,
          secao: metadados?.secao || null,
        };
      }),
    };
  }, [dados, metadadosPorPergunta]);

  return {
    dados: dadosComMetadados,
    isLoading: isLoadingDados || isLoadingFormulario,
  };
}
